import { defineStore } from 'pinia';
import $http from '@nuxt-common/util/app/utils/http';
import * as logger from '@vue-util/universalLogger';
import { fireTag } from '@nuxt-common/util/app/utils/tagManager';
import separateEntityNotificationMessage from '~/utils/SeparateEntityMessage';
import { useSlideoutStore } from '@nuxt-common/atomic-ui/app/stores/useSlideoutStore';
import resolveTier from '~/utils/TierName';
import { offerNotifications, genericErrorMessages } from 'config';
import useBrand from '../utils/useBrand';
import { useUserSessionId } from '../composables/useUserSessionId';

const { enhanceRiskifiedPayload } = useUserSessionId();

const USER_WALLET = '/api/marketing/v1/wallet/{customer_id}/wallet?_expand=rewardcard&sort=expiration';
const STARREWARD = '/xapi/loyalty/v1/starrewardssummary?_pageType=WalletUI';
const FETCHTENDER = '/account-xapi/api/wallet/credit/summary?cardType=Wallet';
const ADD_OFFERS = '/api/marketing/v1/wallet/{customer_id}/offer'; // '/xapi/wallet/v1/addoffer?ocd=';
const REMOVE_OFFER = '/api/marketing/v1/wallet/{customer_id}/offers/{offer_code}'; // '/xapi/wallet/v1/removeOffer?ocd=';
const GET_UPDATED_OFFER = '/api/marketing/v1/wallet/{customer_id}/offers';
const GET_CUSTOMER_OFFER = '/xapi/wallet/v1/customeroffers';
const FETCHUSER_PROFILE = '/account-xapi/api/userprofile/user/{userId}?cardType=shipping';
const ACCOUNT_SUMMARY = '/xapi/loyalty/v1/accountsummary?_pageType=WalletUI';
const BEAUTYBOX_SUBSCRIPTION = '/xapi/subscription/v1/beautybox/userinfo';

const { isMcom, isBcom } = useBrand();

const logSuccess = (requestInfo, responseData) => {
  logger.info(`Successful response for request: ${JSON.stringify(requestInfo)}: ${JSON.stringify(responseData)}`);
};

const fireErrorTag = (label, message, code = '') => {
  let device;
  if (import.meta.client) {
    const isMobile = navigator.userAgent.match(/Mobi/i);
    device = isMobile ? 'mobile' : 'desktop';
  }
  const fireTagData = {
    event_name: 'my account error',
    error_category: 'my account',
    error_code: [code],
    error_label: [label],
    error_message: [message],
    error_misc: [''],
    error_scope: ['global'],
  };
  if (code === '09209') {
    if (device) {
      fireTagData.device = [device];
    }
    fireTagData.error_misc = ['riskified_error_message'];
  }
  if (code === '30002') {
    fireTagData.event_name = 'google recaptcha error';
    fireTagData.error_category = 'my wallet';
  }
  fireTag('link', fireTagData, { immediate: true });
};

export const useWalletStore = defineStore('wallet', {
  state: () => ({
    extendToken: false,
    wallet: [],
    offerLoader: true,
    paymentLoader: true,
    macysMoneyLoader: true,
    starrewardloading: true,
    starrewarderrors: '',
    tierName: '',
    starRewardsSummary: '',
    messages: null,
    cards: '',
    cardType: null,
    pointsTrackerMediaPool: '',
    ui: {
      overlay: {
        props: {
          id: '',
          title: '',
          width: '',
        },
        footer: null,
        component: null,
      },
    },
    tender: null,
    macysMoney: [],
    macysMoneyCard: null,
    newOfferSaved: {},
    paymentMethodError: false,
    addOfferNotification: null,
    addOfferErrorMessage: false,
    macysMoneyError: false,
    hideMacysMoney: true,
    offerPromoShouldDisable: '',
    allOfferAddedCode: [],
    customerOffers: [],
    sessionTimeout: false,
    userProfile: [],
    userEmail: '',
    accountSummary: {},
    points: {
      pointsToNextAward: '',
      totalAvailablePoints: '',
      totalPendingPoints: '',
    },
    rewardCards: [],
    errorNotification: {
      visible: false,
      closable: false,
      severity: 'error',
      message: '',
    },
    showNotifications: {
      visible: false,
      closable: false,
      severity: '',
      message: '',
    },
    rewardCardError: {
      visible: false,
      closable: false,
      severity: '',
      message: '',
    },
    rewardCardLink: null,
    beautySubscription: null,
    invalidCustomer: null,
    successCardType: null,
    successCardId: null,
    slideoutStack: [{ id: 'null' }],
    slideOutFlag: null,
  }),
  actions: {
    setErrorNotification(errorNotification) {
      this.errorNotification = errorNotification;
    },
    setNotification(notification) {
      this.showNotifications = notification;
    },
    setSuccessCardType(data) {
      this.successCardType = data?.cardType;
      this.successCardId = data?.cardId;
    },
    setModalOverlay(overlayDetails) {
      this.ui.overlay = overlayDetails;
    },
    setExtendToken(data) {
      this.extendToken = data;
    },
    setCardGuidError(data) {
      this.invalidCustomer = data;
    },
    setOffer(offers) {
      if (this.wallet) {
        this.wallet.offers = offers?.wallet?.offers;
      }
    },
    setCustomerOffers(data) {
      this.customerOffers = data;
    },
    setPaymentMethodError(cards) {
      this.tender = cards;
      this.paymentMethodError = true;
      this.paymentLoader = false;
    },
    setAddOfferNotification(params) {
      this.addOfferNotification = null;
      const type = params?.type;
      if (type === 'success') {
        this.addOfferNotification = {
          message: offerNotifications.success,
          type,
        };
      } else if (type === 'error' && params?.errors) {
        const error = params?.errors?.error?.[0];
        const errorCode = error?.errorCode?.toString();
        let message = '';
        if (errorCode === '09030' || errorCode === '09038') {
          message = offerNotifications.invalidCode;
        } else if (errorCode === '09041' || errorCode === '54041') {
          message = offerNotifications.alreadyInWallet;
        } else {
          message = error?.message;
        }
        this.addOfferNotification = { message, type };
      }
    },
    setErrorMessage(params) {
      const errors = params?.errors;
      const error = errors?.error?.[0];
      const refs = params?.refs;
      if (errors) {
        if (error?.errorCode?.toString() === '09041') {
          this.addOfferErrorMessage = offerNotifications.alreadyInWallet;
          if (refs) {
            refs.focus();
            refs.style.borderColor = '#de3412';
          }
        } else if (!error?.errorCode) {
          this.addOfferErrorMessage = error?.message;
        }
      } else {
        this.addOfferErrorMessage = false;
      }
    },
    setOfferDisabledPromoCode(data) {
      this.allOfferAddedCode.push(data);
      this.offerPromoShouldDisable = data;
    },
    setSessionTimeout(payload) {
      this.sessionTimeout = payload;
    },
    async fetchUserWallet({ customerId, headers }) {
      const CUSTID_USER_WALLET = USER_WALLET.replace('{customer_id}', customerId);
      const requestInfo = {
        method: 'GET',
        url: CUSTID_USER_WALLET,
        headers,
      };
      try {
        const response = await $http.get(CUSTID_USER_WALLET, { headers });
        if (response?.data?.errors) {
          throw response.data;
        }
        this.wallet = response.data.wallet;
        this.messages = response.data.wallet.messages;
        this.offerLoader = false;
        this.macysMoney = response.data.wallet.rewardCards.rewardcard ? response.data.wallet.rewardCards.rewardcard : [];

        if (!response.data.wallet.rewardCards?.rewardcard?.length) {
          this.hideMacysMoney = false;
        }
        this.macysMoneyLoader = false;
        this.offerError = false;
        this.macysMoneyError = false;
        logSuccess(requestInfo, response.data);
      } catch (err) {
        const message = err?.errors?.error?.[0]?.message;
        if (err?.errors?.httpStatus === 400) {
          this.wallet = {
            error: message,
          };
          this.macysMoneyError = message;
          this.offerLoader = false;
          this.macysMoneyLoader = false;
          logger.error(message);
        } else if (message) {
          const error = {
            visible: true,
            closable: false,
            severity: 'error',
            message,
          };
          this.setNotification(error);
          this.offerLoader = true;
          this.macysMoneyLoader = true;
          logger.error(message);
        }
      }
    },
    async fetchStarRewards({ headers }) {
      const requestInfo = {
        method: 'GET',
        url: STARREWARD,
        headers,
      };
      try {
        const response = await $http.get(STARREWARD, { headers });
        this.starRewardsSummary = response.data;
        this.tierName = response?.data?.tierInfo?.tierName;
        this.pointsTrackerMediaPool = response?.data?.tierInfo?.pointsTrackerMediaPool;
        this.starrewardloading = false;
        this.starrewarderrors = response?.data?.errors || '';
        logSuccess(requestInfo, response.data);
      } catch (err) {
        const error = {
          visible: true,
          closable: false,
          severity: 'error',
          message: err?.errors?.error?.[0]?.message,
        };
        logger.error(error.message);
        this.setNotification(error);
      }
    },
    async fetchTender({ headers }) {
      const requestInfo = {
        method: 'POST',
        url: FETCHTENDER,
        headers,
      };
      try {
        const response = await $http.post(FETCHTENDER, {}, { headers });
        if (response.data?.WalletInfo?.error) {
          const errorCode = response.data.WalletInfo.error.errorCode?.toString();
          const extendToken = response.data.WalletInfo.extendToken;

          // Soft Login session timeout case
          if (errorCode === '45001') {
            //  If backend allows soft login
            if (extendToken) {
              this.setExtendToken(extendToken);
              return; // stop here, overlay will open
            }
            this.setSessionTimeout(true);
            throw response.data;
          }
          // Other errors → throw normally
          throw response.data;
        }

        // separate entity notification check
        const primaryCard = response.data.WalletInfo?.totalCreditCards?.filter((card) => card.primary || false);
        if (primaryCard?.length) {
          if (primaryCard[0].notifyInd) {
            const message = separateEntityNotificationMessage(primaryCard[0]);
            const error = {
              visible: true,
              closable: false,
              severity: 'error',
              message,
            };
            this.setNotification(error);
            const fireTagData = {
              event_name: 'separate entity message',
              error_category: 'wallet',
              error_code: [''],
              error_label: [''],
              error_message: [message.replaceAll(/<[^>]*>/g, '')],
              error_misc: ['wallet'],
              error_scope: ['global'],
              separate_entity: true,
            };
            fireTag('link', fireTagData);
          }
        }
        this.tender = response.data.WalletInfo;
        this.tender.last4digits = this.tender.last4;
        this.paymentLoader = false;
        this.paymentMethodError = false;
        this.setExtendToken(response.data.WalletInfo.extendToken);
        logSuccess(requestInfo, response.data);
      } catch (err) {
        if (err.WalletInfo) {
          logger.error(err.WalletInfo.error.message);
          this.setPaymentMethodError(err);
          const errorCode = err?.WalletInfo?.error?.errorCode;
          const errorCode1 = err?.response?.data?.errors.error?.[0].errorCode;
          if (errorCode?.toString() === '09007' || errorCode1?.toString() === '02001') {
            this.setSessionTimeout(true);
          }
        } else {
          const error = {
            visible: true,
            closable: false,
            severity: 'error',
            message: err?.errors?.error?.[0]?.message,
          };
          logger.error(error.message);
          this.setNotification(error);
        }
      }
    },
    async fetchBeautyboxSubscription() {
      const headers = {
        'X-Macys-ClientId': 'wallet-ui',
        'x-requested-with': 'XMLHttpRequest',
        'Content-Type': 'application/json',
      };
      try {
        const response = await $http.get(BEAUTYBOX_SUBSCRIPTION, {
          data: null,
          headers,
        });
        this.beautySubscription = response.data.user;
      } catch (err) {
        const error = {
          visible: true,
          closable: false,
          severity: 'error',
          message: err?.errors?.error?.[0]?.message,
        };
        logger.error(error.message);
        if (error.message) {
          this.setNotification(error);
        }
      }
    },
    async fetchUserProfile({ headers, userId }) {
      const USER_PROFILE = FETCHUSER_PROFILE.replace('{userId}', userId);
      const requestInfo = {
        method: 'POST',
        url: USER_PROFILE,
        headers,
      };
      try {
        const response = await $http.post(USER_PROFILE, {}, { headers });
        if (response.data) {
          if (response.data.userProfileDetails?.address?.shippingAddresses) {
            this.userProfile = response.data.userProfileDetails.address.shippingAddresses;
          } else {
            this.userProfile = [];
          }
          this.userEmail = response.data.userProfileDetails.email;
          logSuccess(requestInfo, response.data);
        }
      } catch (err) {
        const error = {
          visible: true,
          closable: false,
          severity: 'error',
          message: err?.errors?.error?.[0]?.message,
        };
        logger.error(error.message);
        this.setNotification(error);
      }
    },
    async getUpdatedOffers(dataItems) {
      const CUSTID_UPDATED_OFFERS = GET_UPDATED_OFFER.replace('{customer_id}', dataItems?.customerId);
      const requestInfo = {
        method: 'GET',
        url: CUSTID_UPDATED_OFFERS,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      };
      try {
        const resp = await $http.get(CUSTID_UPDATED_OFFERS, requestInfo);
        if (isBcom) {
          if (dataItems?.newOffer) {
            resp?.data?.wallet?.offers?.map((el) => {
              if (el.offerCode === dataItems?.newOffer) {
                el.justAdded = true;
              }
              return true;
            });
          }
          this.setOffer(resp.data);
        } else {
          this.setOffer(resp.data);
          this.setSlideOut(true);
        }
        logSuccess(requestInfo, resp.data);
      } catch (err) {
        const error = {
          visible: true,
          closable: false,
          severity: 'error',
          message: err?.error?.[0]?.message,
        };
        this.setSlideOut(false);
        logger.error(error.message);
        this.setNotification(error);
      }
    },
    async getCustomerOffers({ headers }) {
      const requestInfo = {
        method: 'GET',
        url: GET_CUSTOMER_OFFER,
        headers,
      };
      try {
        const resp = await $http.get(GET_CUSTOMER_OFFER, { headers });
        this.setCustomerOffers(resp.data);
        logSuccess(requestInfo, resp.data);
      } catch (err) {
        const error = {
          visible: true,
          closable: false,
          severity: 'error',
          message: err?.error?.[0]?.message,
        };
        logger.error(error.message);
        this.setNotification(error);
      }
    },
    async addOffer(param) {
      this.setAddOfferNotification(null);
      const CUSTID_ADD_OFFERS = ADD_OFFERS.replace('{customer_id}', param.customerId);
      const params = {
        offer: {
          offerCode: param.ocd,
        },
      };
      const requestInfo = {
        method: 'POST',
        url: CUSTID_ADD_OFFERS,
        body: JSON.stringify(params),
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      };
      try {
        const response = await $http.post(CUSTID_ADD_OFFERS, params, requestInfo);
        if (response.data.errors) {
          throw response.data;
        }
        const offer = response.data.wallet.offers[0];
        const newSavedOffer = this.newOfferSaved?.offerCode;
        if (newSavedOffer && newSavedOffer.toLowerCase() === offer.offerCode.toLowerCase()) {
          const erroCode = {
            errors: {
              error: [
                {
                  errorCode: 54041,
                  serviceErrorCode: 9041,
                  message: offerNotifications.alreadyInWallet,
                },
              ],
            },
          };
          throw erroCode;
        }
        this.newOfferSaved = offer;
        this.addOfferNotification = null;
        this.addOfferErrorMessage = false;
        this.setAddOfferNotification({ type: 'success' });
        this.setOfferDisabledPromoCode(param.ocd);
        logSuccess(requestInfo, response.data);
      } catch (err) {
        if (err?.errors) {
          if (isBcom) {
            const paramsOfferCode = params?.offer?.offerCode?.toString()?.toUpperCase();
            let savedOffer = '';
            const availableWalletOffers = this.wallet?.offers || '';
            if (availableWalletOffers) {
              availableWalletOffers.map((el) => {
                if (el?.offerCode?.toUpperCase() === paramsOfferCode) {
                  savedOffer = el.offerCode;
                }
                return true;
              });
            }
            const allErrorCode = err?.errors?.error?.[0]?.errorCode?.toString();
            if (allErrorCode === '09030' || allErrorCode === '09038') {
              this.setAddOfferNotification({
                errors: err.errors,
                type: 'error',
              });
            } else if (
              savedOffer?.toUpperCase() === paramsOfferCode &&
              savedOffer?.toUpperCase() !== params?.offer?.offerCode &&
              allErrorCode === '09041'
            ) {
              this.setAddOfferNotification({
                errors: err.errors,
                type: 'error',
              });
            } else if (allErrorCode === '09041') {
              this.setAddOfferNotification({ errors: err.errors, type: 'error' });
            } else if (err.errors) {
              this.setAddOfferNotification({
                errors: err.errors,
                type: 'error',
              });
            }
          } else if (isMcom) {
            this.setAddOfferNotification({ errors: err.errors, type: 'error' });
          }
        } else {
          const error = {
            visible: true,
            closable: false,
            severity: 'error',
            message: err?.error?.[0]?.message,
          };
          logger.error(error.message);
          this.setNotification(error);
        }
        logger.error(err);
        this.setNotification(err);
        this.setSlideOut(false);
      }
    },
    async removeWallet({ offerCode, customerId }) {
      const CUSTID_DELETE_OFFERS = REMOVE_OFFER.replace('{customer_id}', customerId).replace('{offer_code}', offerCode);
      const requestInfo = {
        method: 'DELETE',
        url: CUSTID_DELETE_OFFERS,
      };
      try {
        const response = await $http.delete(CUSTID_DELETE_OFFERS);
        if (response) {
          const successMessage = {
            visible: true,
            closable: false,
            severity: 'success',
            message: offerNotifications.success,
          };
          this.setNotification(successMessage);
          if (offerCode && this.wallet) {
            this.wallet.offers = this.wallet?.offers.filter((el) => el.offerCode !== offerCode);
            this.newOfferSaved = {};
          }
        }
        logSuccess(requestInfo, response.data);
      } catch (error) {
        logger.error(error);
        this.setNotification(error);
      }
    },
    async fetchMacysMoneyCardDetails({ userguid, cardguid, secureUserToken }) {
      const URL = `/api/loyalty/v1/customer/${userguid}/rewardCard/${cardguid}`;

      const requestInfo = {
        method: 'GET',
        url: URL,
      };
      try {
        const response = await $http.get(URL, {
          headers: {
            'x-requested-with': 'XMLHttpRequest',
            ...(secureUserToken && {
              'X-Macys-SecurityToken': decodeURIComponent(secureUserToken),
            }),
          },
          params: { dataunmask: true },
        });
        if (response.data?.rewardcard) {
          this.macysMoneyCard = response.data.rewardcard;
        }
        logSuccess(requestInfo, response.data);
      } catch (err) {
        const errorCode = err?.response?.data?.errors?.error?.[0]?.errorCode;
        if (errorCode?.toString() === '07059' || errorCode?.toString() === '09007' || !cardguid) {
          this.setCardGuidError(true);
        }
        const error = {
          visible: true,
          closable: false,
          severity: 'error',
          message: process.env.ENTRY_POINT === 'bcom' ? err?.error?.[0]?.message : err?.response?.data?.errors?.error?.[0]?.message,
        };
        logger.error(error.message);
        this.setNotification(error);
      }
    },
    async fetchAccountSummary({ headers }) {
      const requestInfo = {
        method: 'GET',
        url: ACCOUNT_SUMMARY,
        headers,
      };
      try {
        const response = await $http.get(ACCOUNT_SUMMARY, { headers });
        if (response?.data?.loyalty?.errors && !response?.data?.loyalty?.rewardCards?.length) {
          throw response.data.loyalty;
        } else if (response?.data?.loyalty?.errors && response?.data?.loyalty?.rewardCards?.length) {
          this.rewardCards = response.data?.loyalty?.rewardCards;
        } else {
          this.accountSummary = response.data;
          this.rewardCards = response.data?.loyalty?.rewardCards;
          this.tierName = resolveTier(response.data?.loyalty?.program?.tierName);
          this.pointsTrackerMediaPool = response?.data?.tierInfo?.pointsTrackerMediaPool;
          this.accountSummaryloading = false;
          this.accountSummaryerrors = response.data?.errors;
          this.points = response.data?.loyalty?.points;
        }
        logSuccess(requestInfo, response.data);
        return response;
      } catch (err) {
        let errorMessage;
        // This Error Code 45073 is for no loyallist account
        if (err?.response?.data?.errors?.error?.[0]?.errorCode === 45073 || err?.errors?.error?.[0]?.errorCode === 45073) {
          errorMessage = null;
        } else if (err?.response?.data?.errors?.error?.[0]?.message) {
          errorMessage = err.response.data.errors.error[0].message;
        } else {
          // Default error handling
          errorMessage = err?.errors?.error?.[0]?.message ? err.errors.error[0].message : err;
        }
        const error = {
          visible: true,
          closable: false,
          severity: 'error',
          message: errorMessage,
        };
        logger.error(err);
        this.setNotification(error);
        throw err;
      }
    },
    async fetchOffers({ customerId }) {
      const CUST_OFFERS = GET_UPDATED_OFFER.replace('{customer_id}', customerId);
      const requestInfo = {
        method: 'GET',
        url: CUST_OFFERS,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      };
      try {
        const resp = await $http.get(CUST_OFFERS, requestInfo);
        this.setOffer(resp.data);
        logSuccess(requestInfo, resp.data);
      } catch (err) {
        const error = {
          visible: true,
          closable: false,
          severity: 'error',
          message: err?.error?.[0]?.message,
        };
        logger.error(error.message);
        this.setNotification(error);
      }
    },
    async updateCard(payload) {
      const URL = '/account-xapi/api/wallet/updateTender';
      await enhanceRiskifiedPayload(payload?.updatedCard);
      try {
        const response = await $http.put(URL, payload?.updatedCard);
        if (response.status === 200 && response.data.walletTender.msg) {
          return response;
        }
        const validationError = response?.data?.walletTender;
        const errorMessages = validationError.errors ? validationError.errors.error?.[0].message : validationError.error.message;
        const errorCode = validationError.errors ? validationError.errors.error?.[0].errorCode : validationError.error.errorCode || '';
        fireErrorTag('wallet edit card', errorMessages, errorCode);
        throw new Error(errorMessages);
      } catch (error) {
        fireErrorTag('wallet edit card', error.message);
        this.setErrorNotification({
          visible: true,
          severity: 'error',
          closable: false,
          message: error.message || genericErrorMessages.unavailable,
        });
        logger.error(error);
        this.setNotification(error);
        throw error;
      }
    },
    setAsDefaultCard(newDefaultUserCardGuid, previousDefaultUserCardGuid) {
      return new Promise((resolve, reject) => {
        $http
          .put(`/account-xapi/api/wallet/cards/default`, {
            defaultCardRequest: {
              newDefaultTenderGuid: newDefaultUserCardGuid,
              oldDefaultTenderGuid: previousDefaultUserCardGuid,
            },
          })
          .then((response) => {
            if (response.status === 200 && response.data.defaultCardResponse.updated) {
              resolve(response);
            } else {
              const validationError = response.data?.defaultCardResponse;
              const errorMessages = validationError.updated ? genericErrorMessages.unavailable : validationError.message;
              this.setErrorNotification({ visible: true, severity: 'error', closable: false, message: errorMessages });
              reject(response);
            }
          })
          .catch((error) => {
            this.setErrorNotification({ visible: true, severity: 'error', closable: false, message: error.message });
            reject(error);
          });
      });
    },
    async deleteCard(payload) {
      const URL = '/account-xapi/api/wallet/deleteTender';
      try {
        const response = await $http.delete(URL, { data: payload?.deleteCard });
        if (response.status === 200 && response.data.walletTender.msg === 'success') {
          return response;
        }
        const validationError = response?.data?.walletTender;
        const errorMessages = validationError.errors ? validationError.errors.error?.[0].message : validationError.error.message;
        throw errorMessages;
      } catch (error) {
        this.setErrorNotification({
          visible: true,
          severity: 'error',
          closable: false,
          message: error || genericErrorMessages.unavailable,
        });
        logger.error(error.message);
        this.setNotification(error);
        throw error;
      }
    },
    async postCreditCard({ formData, userId }) {
      const url = `/account-xapi/api/wallet/${userId}/addtenders?includeCardID=true`;
      await enhanceRiskifiedPayload(formData);
      try {
        const response = await $http.post(url, formData);
        if (response.status === 200 && response.data.walletTender.msg) {
          return response;
        }
        const validationError = response?.data?.walletTender;
        const errorMessages = validationError.errors ? validationError.errors.error?.[0].message : validationError.error.message;
        const errorCode = validationError.errors ? validationError.errors.error?.[0].errorCode : validationError.error.errorCode || '';
        fireErrorTag('wallet add card', errorMessages, errorCode);
        throw errorMessages;
      } catch (error) {
        this.setErrorNotification({
          visible: true,
          severity: 'error',
          closable: false,
          message: error || genericErrorMessages.unavailable,
        });
        logger.error(error.message);
        this.setNotification(error);
        throw error;
      }
    },
    async addRewardCard({ userguid, cardguid, secureUserToken }) {
      const URL = `/api/loyalty/v1/customer/${userguid}/rewardCard/${cardguid}`;
      const requestInfo = {
        method: 'POST',
        url: URL,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          ...(secureUserToken && {
            'X-Macys-SecurityToken': decodeURIComponent(secureUserToken),
          }),
        },
      };
      const requestBody = {
        rewardcards: {
          rewardcard: {
            cardGuid: cardguid,
          },
        },
      };
      try {
        const response = await $http.post(URL, requestBody, requestInfo);
        if (response.data.errors) {
          throw response.data;
        }
        this.rewardCardLink = response?.data?.rewardcards?.link;
        logSuccess(requestInfo, response.data);
        return response;
      } catch (error) {
        const errorResponse = error?.response?.data || error;
        if (errorResponse?.errors?.error?.[0]) {
          const errorMessage = errorResponse?.errors?.error?.[0]?.message;
          const err = {
            visible: true,
            closable: false,
            severity: 'error',
            message: errorMessage || '',
          };
          logger.error(err);
          if (isMcom) {
            this.setNotification(err);
          } else {
            this.rewardCardError = err;
          }
        } else {
          logger.error('Unknown error occurred:', error);
        }
        throw error;
      }
    },
    async fetchCreditService({ onlineUID, requestBodyData }) {
      const _source = 'Wallet';
      const _deviceType = 'DESKTOP';
      const requestId = onlineUID + Math.floor(Date.now() / 1000);
      const registrationStatusUrl = `/account-xapi/api/creditservice/${onlineUID}/checkregistrationstatus?_source=${_source}&_deviceType=${_deviceType}`;
      const requestInfo = {
        method: 'POST',
        url: registrationStatusUrl,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'X-Macys-ClientId': 'ShopApp',
          'X-Macys-Request-Id': requestId,
        },
      };
      try {
        const response = await $http.post(registrationStatusUrl, requestBodyData, requestInfo);
        if (response.data.errors || !response?.data?.checkregistrationstatusresponse?.registrationStatusDO?.length) {
          throw response.data;
        }
        logSuccess(requestInfo, response.data);
        return response;
      } catch (error) {
        logger.error(error);
        this.setNotification(error);
        throw error;
      }
    },
    resetSlideout() {
      this.slideoutStack = [{ id: 'null' }];
      const slideout = useSlideoutStore();
      slideout.close('my-wallet-slideout');
    },

    setSlideout(slideoutDetail) {
      this.slideoutStack.unshift(slideoutDetail);
    },
    setMacysMoney(data) {
      if (data?.rewardcard) {
        this.macysMoneyCard = data.rewardcard;
      }
    },
    setSlideOut(flag) {
      this.slideOutFlag = flag;
    },
  },
});
